import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  catchError,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { UserMutationService } from '@main-client/src/app/user/user-mutation.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Account } from '@libs/src/models/account.model';
import { SetUser } from '@libs/src/user/user.actions';
import { IAppState } from '@main-client/src/app/app.state';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-settings-personal-email-change',
  templateUrl: 'settings-personal-email-change.component.html',
})
export class SettingsPersonalEmailChangeComponent implements OnInit {
  @Input() account: Account;
  @Input() isEmailChangeEnabled: boolean;
  isLoading = false;
  emailChangeForm = this.fb.control('');

  constructor(
    private readonly accountBackend: AccountBackend,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly fb: UntypedFormBuilder,
    private readonly store: Store<IAppState>,
    private readonly toastService: ToastService,
    private readonly userMutationService: UserMutationService,
  ) {}

  ngOnInit(): void {
    this.emailChangeForm.setValue(
      this.account.pending_email ?? this.account.email,
    );
  }

  saveForm(form: UntypedFormControl) {
    this.isLoading = true;
    this.accountBackend
      .patchAccount({ email_is_verified: false, pending_email: form.value })
      .pipe(
        switchMap(() => this.sendVerificationEmail(form)),
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError(getServerError(error));
          return EMPTY;
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  cancelEmailChange() {
    this.accountBackend
      .patchAccount({ email_is_verified: true, pending_email: null })
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            $localize`:@@settingsView.emailChange.cancelEmailChangeToast: Email change cancelled!`,
          );
          this.store.dispatch(
            SetUser({
              email_is_verified: true,
              pending_email: null,
            }),
          );
        }),
        finalize(() => {
          this.emailChangeForm.setValue(this.account.email);
          this.isLoading = false;
        }),
        catchError((response) => {
          const errorMessage = getServerError(response);
          this.toastService.showError(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  sendVerificationEmail(form: UntypedFormControl) {
    return this.userMutationService.sendPendingEmail(form.value).pipe(
      catchError((message) => {
        this.toastService.showError(message);
        return EMPTY;
      }),
      tap(({ message }) => {
        this.toastService.showSuccess(message);
        this.store.dispatch(
          SetUser({
            email_is_verified: false,
            pending_email: form.value,
          }),
        );
      }),
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoading = false;
      }),
    );
  }
}
